import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  Button,
  TextField,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Alert,
  Checkbox,
  FormControlLabel,
  IconButton,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Close, ExpandMore, Add } from "@mui/icons-material";
import { format, parseISO, parse } from "date-fns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import getApiBase from "./getApiBase";
import {
  estimatePlatformFeeBreakdown,
  fetchPlatformFeeRate,
  formatUsd,
} from "../utils/platformFee";
import {
  availabilitySlotDateOnly,
  isAvailabilityChipSelected,
  normalizeSelectedSlots,
  partitionSelectedAvailability,
  toggleAvailabilityChip,
} from "../utils/directAvailabilityChips";

// Helper function to convert 24-hour time to 12-hour AM/PM format
const formatTimeToAMPM = (time24) => {
  if (!time24) return "";
  try {
    // Handle both "HH:mm" and "HH:mm:ss" formats
    const timeStr = time24.toString().split(".")[0]; // Remove any milliseconds
    const parsed = parse(
      timeStr,
      timeStr.length > 5 ? "HH:mm:ss" : "HH:mm",
      new Date(),
    );
    return format(parsed, "h:mm a");
  } catch (err) {
    console.error("Error formatting time:", err);
    return time24;
  }
};

// Helper functions (same as in MechanicDashboard)
const convertTo24Hour = (hour12, minute, ampm) => {
  let hour24 = parseInt(hour12);
  if (ampm === "PM" && hour24 !== 12) hour24 += 12;
  if (ampm === "AM" && hour24 === 12) hour24 = 0;
  return `${hour24.toString().padStart(2, "0")}:${minute}`;
};

const parseTo12Hour = (time24) => {
  if (!time24 || time24 === "") return { hour: "9", minute: "00", ampm: "AM" };
  const [hourStr, minuteStr] = time24.split(":");
  if (!hourStr || !minuteStr) return { hour: "9", minute: "00", ampm: "AM" };
  const hour24 = parseInt(hourStr);
  const hour12 =
    hour24 === 0
      ? "12"
      : hour24 > 12
        ? (hour24 - 12).toString()
        : hour24.toString();
  const ampm = hour24 < 12 ? "AM" : "PM";
  return { hour: hour12, minute: minuteStr, ampm };
};

const formatTimeTo12Hour = (time24) => {
  if (!time24) return "";
  const parsed = parseTo12Hour(time24);
  return `${parsed.hour}:${parsed.minute} ${parsed.ampm}`;
};

/** Hrs: text input avoids type=number spinners / stray "0." display */
const filterHrsInput = (input) => {
  let value = input.replace(/[^\d.]/g, "");
  const dot = value.indexOf(".");
  if (dot !== -1) {
    value = value.slice(0, dot + 1) + value.slice(dot + 1).replace(/\./g, "");
  }
  const parts = value.split(".");
  if (parts[1] && parts[1].length > 2) {
    value = parts[0] + "." + parts[1].substring(0, 2);
  }
  return value;
};

const normalizeHrsOnBlur = (raw) => {
  if (raw === undefined || raw === null || raw === "") return "0";
  const s = String(raw).trim();
  if (s === "" || s === ".") return "0";
  if (s.endsWith(".")) {
    const head = s.slice(0, -1);
    return head === "" ? "0" : head;
  }
  return s;
};

const APPT_HOUR_CONTROL_SX = {
  minWidth: 84,
  width: 84,
  flexShrink: 0,
  "& .MuiSelect-select": {
    overflow: "visible",
    textOverflow: "clip",
    paddingRight: "28px !important",
  },
};
const APPT_MIN_CONTROL_SX = { minWidth: 64, flexShrink: 0 };

/** Horizontal scroll for time rows — pad top so outlined labels are not clipped by overflow-x. */
const APPT_TIME_ROW_SX = {
  display: "flex",
  gap: 1,
  mb: 1,
  alignItems: "center",
  flexWrap: "nowrap",
  overflowX: "auto",
  overflowY: "visible",
  maxWidth: "100%",
  pt: 1.25,
};

const displayHrsStored = (raw) => {
  if (raw === undefined || raw === null || raw === "") return "0";
  return String(raw);
};

const ServiceRequestCard = ({
  request,
  shop,
  hasExtendedServiceHistory,
  isExtendedHistoryActive,
  isExtendedHistoryPlanAvailable,
  userRole, // Add userRole prop to check if user is Owner
  needsAttention = false,
  parentColor = "#d32f2f", // '#d32f2f' for Direct, '#4285f4' for Broadcast
  onOpenConversation,
  onExtendedHistoryClick,
  onCancelQuote,
  onRefresh,
  onSubmitBid,
  onShopConfirm,
  billingReadyForQuotes = true,
  onBillingRequired,
  onPriceChange,
  // State props
  bidAmounts,
  setBidAmounts,
  selectedSlot,
  setSelectedSlot,
  proposedHours,
  setProposedHours,
  proposedMinutes,
  setProposedMinutes,
  proposedAmpm,
  setProposedAmpm,
  proposedStartTimes,
  setProposedStartTimes,
  jobDurations,
  setJobDurations,
  counterOffer,
  setCounterOffer,
  counterAvailableDates,
  setCounterAvailableDates,
  selectedCounterDate,
  setSelectedCounterDate,
  altTimeHour,
  setAltTimeHour,
  altTimeMinute,
  setAltTimeMinute,
  altTimeAmpm,
  setAltTimeAmpm,
  // Announcement-specific state
  shopTimeSlots,
  setShopTimeSlots,
  dropoffDates,
  setDropoffDates,
  // Expanded state
  isExpanded,
  onToggleExpanded,
}) => {
  const isDirect = request.request_display_type === "Direct";
  const isBroadcast = request.request_display_type === "Broadcast";

  // Local state for drop-off date/time input (similar to alternative times)
  const [selectedDropoffDate, setSelectedDropoffDate] = useState(null);
  const [dropoffHour, setDropoffHour] = useState("8");
  const [dropoffMinute, setDropoffMinute] = useState("00");
  const [dropoffAmpm, setDropoffAmpm] = useState("AM");
  const [pickupHour, setPickupHour] = useState("5");
  const [pickupMinute, setPickupMinute] = useState("00");
  const [pickupAmpm, setPickupAmpm] = useState("PM");

  // Check if shop already quoted
  const shopBid = request.bids?.find((bid) => bid.shop_name === shop.name);

  // DEBUG: Log to understand what data we have
  if (request.bids && request.bids.length > 0) {
    console.log("🔍 DEBUG - request.bids:", request.bids);
    console.log("🔍 DEBUG - shop.name:", shop.name);
    console.log("🔍 DEBUG - shopBid found:", shopBid);
  }

  const alreadyQuoted = !!shopBid;
  const bidStatus = shopBid?.bid_status || "pending";
  const customerAccepted = bidStatus === "customer_accepted";
  const shopConfirmed = bidStatus === "shop_confirmed";
  const quoteRejected = shopBid?.status === "Rejected";

  // State for changing quote mode
  const [isChangingQuote, setIsChangingQuote] = useState(false);
  const [platformFeeRate, setPlatformFeeRate] = useState(0.03);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const rate = await fetchPlatformFeeRate(getApiBase(), shop?.id);
      if (!cancelled) {
        setPlatformFeeRate(rate);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shop?.id]);

  // Determine pulse color based on parentColor prop
  const pulseColor =
    parentColor === "#d32f2f"
      ? "rgba(211, 47, 47, 0.15)" // Light red
      : "rgba(66, 133, 244, 0.15)"; // Light blue

  // State for multiple time slots per request
  const [timeSlots, setTimeSlots] = useState({}); // { [requestId]: [{ hour, minute, ampm, duration }, ...] }

  // Helper functions for time slots
  const getTimeSlotCount = (requestId) => {
    return timeSlots[requestId]?.length || 0;
  };

  const addTimeSlot = (requestId) => {
    const currentSlots = timeSlots[requestId] || [];
    if (currentSlots.length >= 5) return;
    const { appointment, list } = partitionSelectedAvailability(
      selectedSlot[requestId],
    );
    const sel = appointment[0] || list[0];
    const chipDate = availabilitySlotDateOnly(sel?.slot);
    setTimeSlots((prev) => ({
      ...prev,
      [requestId]: [
        ...currentSlots,
        { date: chipDate, hour: "", minute: "00", ampm: "AM", duration: "0" },
      ],
    }));
  };

  const removeTimeSlot = (requestId, index) => {
    const currentSlots = timeSlots[requestId] || [];
    setTimeSlots((prev) => ({
      ...prev,
      [requestId]: currentSlots.filter((_, i) => i !== index),
    }));
  };

  const updateTimeSlot = (requestId, index, field, value) => {
    const currentSlots = timeSlots[requestId] || [];
    const updatedSlots = [...currentSlots];
    updatedSlots[index] = { ...updatedSlots[index], [field]: value };
    setTimeSlots((prev) => ({
      ...prev,
      [requestId]: updatedSlots,
    }));
  };

  // Initialize first time slot when a preferred-window chip is selected (only when none exist yet)
  useEffect(() => {
    const { appointment } = partitionSelectedAvailability(
      selectedSlot[request.id],
    );
    const sel = appointment[0] || null;
    if (sel && !timeSlots[request.id]?.length) {
      const chipDate = availabilitySlotDateOnly(sel.slot);
      const firstSlot = {
        date: chipDate,
        hour: proposedHours[request.id] || "",
        minute: proposedMinutes[request.id] || "00",
        ampm: proposedAmpm[request.id] || "AM",
        duration: jobDurations[request.id] || "0",
      };
      setTimeSlots((prev) => ({ ...prev, [request.id]: [firstSlot] }));
    }
    // Do NOT clear time slots when toggling chips so mechanic can offer multiple dates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlot[request.id], request.id]);

  // Sync timeSlots to shopTimeSlots (parent) for submit: each slot becomes { date, start, end }
  useEffect(() => {
    const slots = timeSlots[request.id];
    if (!slots?.length) {
      setShopTimeSlots((prev) => {
        const next = { ...prev };
        delete next[request.id];
        return next;
      });
      return;
    }
    const built = slots
      .filter((s) => s.hour && s.minute !== undefined && s.ampm && s.duration)
      .map((s) => {
        const start = convertTo24Hour(s.hour, s.minute, s.ampm);
        const [h, m] = start.split(":").map(Number);
        const durMins = parseFloat(s.duration) * 60;
        const endMins = h * 60 + m + durMins;
        const endH = Math.floor(endMins / 60) % 24;
        const endM = Math.round(endMins % 60);
        const end = `${endH.toString().padStart(2, "0")}:${endM.toString().padStart(2, "0")}`;
        const dateStr = (s.date || "").toString().split("T")[0];
        return dateStr ? { date: dateStr, start, end } : { start, end };
      });
    if (built.length) {
      setShopTimeSlots((prev) => ({ ...prev, [request.id]: built }));
    } else {
      setShopTimeSlots((prev) => {
        const next = { ...prev };
        delete next[request.id];
        return next;
      });
    }
  }, [timeSlots[request.id], request.id, setShopTimeSlots]);

  return (
    <Accordion
      id={`service-request-${request.id}`}
      expanded={isExpanded}
      onChange={onToggleExpanded}
      sx={{ mb: 2, border: "1px solid #e0e0e0" }}
    >
      {/* THIS is what pulses - the summary bar only */}
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          backgroundColor: "#fff",
          animation: needsAttention ? "pulse 2s ease-in-out infinite" : "none",
          "@keyframes pulse": {
            "0%": { backgroundColor: "#fff" },
            "50%": { backgroundColor: pulseColor },
            "100%": { backgroundColor: "#fff" },
          },
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            justifyContent: "space-between",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
          <Typography variant="body1" fontWeight="500">
            {isDirect
              ? `Direct Request #${request.id}`
              : `Broadcast Request #${request.id}`}
            {` from ${request.user}`}
          </Typography>
          {alreadyQuoted && !quoteRejected && (
            <Chip
              label={
                customerAccepted
                  ? "Customer Accepted"
                  : shopConfirmed
                    ? "Confirmed"
                    : "Quote Submitted"
              }
              size="small"
              color="success"
              sx={{
                fontWeight: "bold",
                ...(customerAccepted && {
                  backgroundColor: "#2e7d32",
                  color: "#fff",
                  border: "1px solid #1b5e20",
                }),
                ...(shopConfirmed && {
                  backgroundColor: "#1b5e20",
                  color: "#fff",
                }),
              }}
            />
          )}
          {quoteRejected && (
            <Chip
              label="Quote Declined"
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "#8b0000", // Dark red
                color: "#fff", // White text
                "&:hover": {
                  backgroundColor: "#6b0000",
                },
              }}
            />
          )}
        </Box>
      </AccordionSummary>

      {/* Expanded details - stays white, no pulsing */}
      <AccordionDetails sx={{ backgroundColor: "#fff", p: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            alignItems: { xs: "stretch", md: "flex-start" },
            width: "100%",
          }}
        >
          {/* Left side - Main content (minWidth 0 so nowrap time rows scroll here, not shove quote aside).
              Do not use overflow:hidden — it clips MUI outlined InputLabel text above the fields. */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Request Details
            </Typography>

            {/* Standardized Request Details Section */}
            <Box sx={{ mb: 2 }}>
              <Typography>
                <strong>Car:</strong> {request.car}
              </Typography>
              <Typography>
                <strong>Services:</strong> {request.services}
              </Typography>
              {request.distance && (
                <Typography>
                  <strong>Distance:</strong> {request.distance} miles away
                </Typography>
              )}
              <Typography>
                <strong>Customer's Note:</strong> {request.description}
              </Typography>
            </Box>

            {/* Customer's availability; when customer accepted, show mechanic-offered slots as chips with selected highlighted (same as broadcast) */}
            {request.availableDates && request.availableDates.length > 0 && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: "#e3f2fd",
                  borderRadius: 1,
                  border: "1px solid #2196f3",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="primary"
                  mb={1}
                >
                  {customerAccepted && shopBid?.customer_selected_slot_details
                    ? (() => {
                        const slot = shopBid.customer_selected_slot_details;
                        const dateStr = (slot.available_date || slot.date || "")
                          .toString()
                          .split("T")[0];
                        const isDropoff = !!(
                          slot.is_dropoff === true ||
                          slot.is_dropoff === 1 ||
                          slot.is_dropoff === "true"
                        );
                        const timeStr =
                          slot.start_time && slot.end_time
                            ? ` at ${formatTimeTo12Hour(slot.start_time)} – ${formatTimeTo12Hour(slot.end_time)}`
                            : isDropoff
                              ? " — times as agreed"
                              : "";
                        return `✅ Appointment (customer selected: ${dateStr}${timeStr})`;
                      })()
                    : "📅 Customer's availability (select every option to include on your quote — click again to remove):"}
                </Typography>
                {customerAccepted && shopBid ? (
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {(request.mechanic_available_dates || [])
                      .filter(
                        (d) =>
                          d.source === "Mechanic" &&
                          d.shop_id === shopBid?.autoshop_id,
                      )
                      .map((slot, i) => {
                        const dateStr = (slot.available_date || slot.date || "")
                          .toString()
                          .split("T")[0];
                        const isDropoff = !!(
                          slot.is_dropoff === true ||
                          slot.is_dropoff === 1 ||
                          slot.is_dropoff === "true"
                        );
                        const label = isDropoff
                          ? `🚗 ${format(parseISO(dateStr), "EEE, MMM d, yyyy")} (Drop-off: ${formatTimeToAMPM(slot.start_time)} – Pickup: ${formatTimeToAMPM(slot.end_time)})`
                          : `${format(parseISO(dateStr), "EEE, MMM d, yyyy")} (${formatTimeToAMPM(slot.start_time)} – ${formatTimeToAMPM(slot.end_time)})`;
                        const norm = (t) =>
                          (t || "").toString().trim().substring(0, 5);
                        const sel = shopBid?.customer_selected_slot_details;
                        const selected =
                          sel &&
                          String(slot.available_date || slot.date || "").split(
                            "T",
                          )[0] ===
                            String(sel.available_date || sel.date || "").split(
                              "T",
                            )[0] &&
                          norm(slot.start_time) === norm(sel.start_time) &&
                          norm(slot.end_time) === norm(sel.end_time);
                        return (
                          <Chip
                            key={slot.id != null ? slot.id : i}
                            label={label}
                            color={selected ? "primary" : "default"}
                            variant={selected ? "filled" : "outlined"}
                            sx={selected ? { fontWeight: 600 } : {}}
                          />
                        );
                      })}
                  </Stack>
                ) : (
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {request.availableDates.map((slot, i) => {
                      const slotDate = slot?.date ?? slot?.available_date;
                      if (!slot || !slotDate) return null;
                      const isDropoffOnly =
                        slot.is_dropoff || (!slot.start_time && !slot.end_time);
                      const dateOnly =
                        typeof slotDate === "string" && slotDate.split
                          ? slotDate.split("T")[0]
                          : slotDate;
                      let label;
                      if (isDropoffOnly) {
                        label = `Drop-off: ${format(parseISO(dateOnly), "EEE, MMM d, yyyy")} — you set times (e.g. 1hr after open, pickup 1hr before close)`;
                      } else if (slot.start_time && slot.end_time) {
                        const start = parseISO(
                          `${dateOnly}T${slot.start_time}`,
                        );
                        const end = parseISO(`${dateOnly}T${slot.end_time}`);
                        label = `${format(parseISO(dateOnly), "EEE, MMM d, yyyy")} (${format(start, "h:mm a")} – ${format(end, "h:mm a")})`;
                      } else return null;

                      const isSelected = isAvailabilityChipSelected(
                        selectedSlot[request.id],
                        i,
                      );

                      return (
                        <Chip
                          key={i}
                          label={label}
                          clickable={
                            !alreadyQuoted || isChangingQuote || quoteRejected
                          }
                          color={isSelected ? "primary" : "default"}
                          variant={isSelected ? "filled" : "outlined"}
                          onClick={() => {
                            if (
                              !alreadyQuoted ||
                              isChangingQuote ||
                              quoteRejected
                            ) {
                              setSelectedSlot((prev) => {
                                const nextList = toggleAvailabilityChip(
                                  prev[request.id],
                                  i,
                                  slot,
                                );
                                const next = { ...prev };
                                if (nextList.length === 0) {
                                  delete next[request.id];
                                } else {
                                  next[request.id] = nextList;
                                }
                                return next;
                              });
                              // Prefill appointment clock from preferred-window chip only
                              if (
                                !isDropoffOnly &&
                                slot.start_time &&
                                !isSelected
                              ) {
                                setProposedStartTimes((prev) => ({
                                  ...prev,
                                  [request.id]: String(slot.start_time).slice(
                                    0,
                                    5,
                                  ),
                                }));
                              }
                              // When fully cleared, clear proposed time fields
                              if (isSelected) {
                                const remaining = toggleAvailabilityChip(
                                  selectedSlot[request.id],
                                  i,
                                  slot,
                                );
                                if (remaining.length === 0) {
                                  setProposedHours((prev) => {
                                    const newState = { ...prev };
                                    delete newState[request.id];
                                    return newState;
                                  });
                                  setProposedMinutes((prev) => {
                                    const newState = { ...prev };
                                    delete newState[request.id];
                                    return newState;
                                  });
                                  setProposedAmpm((prev) => {
                                    const newState = { ...prev };
                                    delete newState[request.id];
                                    return newState;
                                  });
                                  setProposedStartTimes((prev) => {
                                    const newState = { ...prev };
                                    delete newState[request.id];
                                    return newState;
                                  });
                                  setJobDurations((prev) => {
                                    const newState = { ...prev };
                                    delete newState[request.id];
                                    return newState;
                                  });
                                }
                              }
                            }
                          }}
                        />
                      );
                    })}
                  </Stack>
                )}
              </Box>
            )}

            {/* Time Selection UI - Multiple Slots (preferred-window chips) */}
            {partitionSelectedAvailability(selectedSlot[request.id]).appointment
              .length > 0 &&
              (!alreadyQuoted || isChangingQuote || quoteRejected) && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1, fontWeight: 600 }}
                  >
                    Appointment start time
                  </Typography>
                  {(timeSlots[request.id] || []).map((slot, index) => {
                    // Helper function to calculate finish time
                    const calculateFinishTime = (
                      hour,
                      minute,
                      ampm,
                      duration,
                    ) => {
                      if (!hour || minute === undefined || !ampm || !duration)
                        return "";
                      try {
                        const startTime = convertTo24Hour(hour, minute, ampm);
                        const [h, m] = startTime.split(":").map(Number);
                        const totalMins =
                          h * 60 + m + parseFloat(duration) * 60;
                        const endH = Math.floor(totalMins / 60) % 24;
                        const endM = Math.round(totalMins % 60);
                        const period = endH >= 12 ? "PM" : "AM";
                        const displayH =
                          endH === 0 ? 12 : endH > 12 ? endH - 12 : endH;
                        return `${displayH}:${String(endM).padStart(2, "0")} ${period}`;
                      } catch {
                        return "";
                      }
                    };

                    // Sync first slot with existing state for backward compatibility
                    const slotHour =
                      index === 0
                        ? proposedHours[request.id] || slot.hour
                        : slot.hour;
                    const slotMinute =
                      index === 0
                        ? proposedMinutes[request.id] || slot.minute
                        : slot.minute;
                    const slotAmpm =
                      index === 0
                        ? proposedAmpm[request.id] || slot.ampm
                        : slot.ampm;
                    const slotDuration =
                      index === 0
                        ? (jobDurations[request.id] ?? slot.duration ?? "0")
                        : (slot.duration ?? "0");

                    const slotDateStr = (slot.date || "")
                      .toString()
                      .split("T")[0];
                    const dateLabel = slotDateStr
                      ? format(parseISO(slotDateStr), "EEE, MMM d, yyyy")
                      : "";

                    return (
                      <Box key={index} sx={APPT_TIME_ROW_SX}>
                        {dateLabel && (
                          <Chip
                            size="small"
                            label={dateLabel}
                            sx={{ alignSelf: "center", mr: 0.5 }}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            alignItems: "center",
                            flexWrap: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          <FormControl size="small" sx={APPT_HOUR_CONTROL_SX}>
                            <InputLabel
                              id={`appt-start-hour-${request.id}-${index}`}
                              shrink
                            >
                              Hour
                            </InputLabel>
                            <Select
                              labelId={`appt-start-hour-${request.id}-${index}`}
                              label="Hour"
                              notched
                              displayEmpty
                              value={slotHour ?? ""}
                              renderValue={(selected) =>
                                selected !== "" && selected != null
                                  ? String(selected)
                                  : "00"
                              }
                              onChange={(e) => {
                                const hour = e.target.value;
                                if (index === 0) {
                                  setProposedHours((prev) => ({
                                    ...prev,
                                    [request.id]: hour,
                                  }));
                                  updateTimeSlot(
                                    request.id,
                                    index,
                                    "hour",
                                    hour,
                                  );
                                  if (
                                    hour &&
                                    slotMinute !== undefined &&
                                    slotAmpm
                                  ) {
                                    const time24 = convertTo24Hour(
                                      hour,
                                      slotMinute,
                                      slotAmpm,
                                    );
                                    setProposedStartTimes((prev) => ({
                                      ...prev,
                                      [request.id]: time24,
                                    }));
                                  }
                                } else {
                                  updateTimeSlot(
                                    request.id,
                                    index,
                                    "hour",
                                    hour,
                                  );
                                }
                              }}
                            >
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                                (h) => (
                                  <MenuItem key={h} value={h.toString()}>
                                    {h}
                                  </MenuItem>
                                ),
                              )}
                            </Select>
                          </FormControl>
                          <Typography variant="body2">:</Typography>
                          <FormControl size="small" sx={{ minWidth: 60 }}>
                            <InputLabel>Min</InputLabel>
                            <Select
                              value={slotMinute !== undefined ? slotMinute : ""}
                              label="Min"
                              onChange={(e) => {
                                const minute = e.target.value;
                                if (index === 0) {
                                  setProposedMinutes((prev) => ({
                                    ...prev,
                                    [request.id]: minute,
                                  }));
                                  updateTimeSlot(
                                    request.id,
                                    index,
                                    "minute",
                                    minute,
                                  );
                                  if (
                                    slotHour &&
                                    minute !== undefined &&
                                    slotAmpm
                                  ) {
                                    const time24 = convertTo24Hour(
                                      slotHour,
                                      minute,
                                      slotAmpm,
                                    );
                                    setProposedStartTimes((prev) => ({
                                      ...prev,
                                      [request.id]: time24,
                                    }));
                                  }
                                } else {
                                  updateTimeSlot(
                                    request.id,
                                    index,
                                    "minute",
                                    minute,
                                  );
                                }
                              }}
                            >
                              <MenuItem value="00">00</MenuItem>
                              <MenuItem value="15">15</MenuItem>
                              <MenuItem value="30">30</MenuItem>
                              <MenuItem value="45">45</MenuItem>
                            </Select>
                          </FormControl>
                          <FormControl size="small" sx={{ minWidth: 70 }}>
                            <InputLabel>AM/PM</InputLabel>
                            <Select
                              value={slotAmpm || "AM"}
                              label="AM/PM"
                              onChange={(e) => {
                                const ampm = e.target.value;
                                if (index === 0) {
                                  setProposedAmpm((prev) => ({
                                    ...prev,
                                    [request.id]: ampm,
                                  }));
                                  updateTimeSlot(
                                    request.id,
                                    index,
                                    "ampm",
                                    ampm,
                                  );
                                  if (
                                    slotHour &&
                                    slotMinute !== undefined &&
                                    ampm
                                  ) {
                                    const time24 = convertTo24Hour(
                                      slotHour,
                                      slotMinute,
                                      ampm,
                                    );
                                    setProposedStartTimes((prev) => ({
                                      ...prev,
                                      [request.id]: time24,
                                    }));
                                  }
                                } else {
                                  updateTimeSlot(
                                    request.id,
                                    index,
                                    "ampm",
                                    ampm,
                                  );
                                }
                              }}
                            >
                              <MenuItem value="AM">AM</MenuItem>
                              <MenuItem value="PM">PM</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        <TextField
                          label="Hrs"
                          size="small"
                          type="text"
                          inputMode="decimal"
                          inputProps={{ style: { textAlign: "center" } }}
                          value={slotDuration}
                          onChange={(e) => {
                            let value = filterHrsInput(e.target.value);
                            if (value === "") value = "0";
                            if (index === 0) {
                              setJobDurations((prev) => ({
                                ...prev,
                                [request.id]: value,
                              }));
                              updateTimeSlot(
                                request.id,
                                index,
                                "duration",
                                value,
                              );
                            } else {
                              updateTimeSlot(
                                request.id,
                                index,
                                "duration",
                                value,
                              );
                            }
                          }}
                          onBlur={(e) => {
                            const next = normalizeHrsOnBlur(e.target.value);
                            if (index === 0) {
                              setJobDurations((prev) => ({
                                ...prev,
                                [request.id]: next,
                              }));
                              updateTimeSlot(
                                request.id,
                                index,
                                "duration",
                                next,
                              );
                            } else {
                              updateTimeSlot(
                                request.id,
                                index,
                                "duration",
                                next,
                              );
                            }
                          }}
                          sx={{ minWidth: "100px", width: "100px" }}
                        />

                        <TextField
                          label="Est. Finish Time"
                          size="small"
                          value={calculateFinishTime(
                            slotHour,
                            slotMinute,
                            slotAmpm,
                            slotDuration,
                          )}
                          InputProps={{ readOnly: true }}
                          sx={{
                            minWidth: "140px",
                            width: "140px",
                            flexShrink: 0,
                          }}
                        />

                        {index > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => removeTimeSlot(request.id, index)}
                            sx={{ alignSelf: "center" }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}

                  {getTimeSlotCount(request.id) < 5 && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => addTimeSlot(request.id)}
                      sx={{ mt: 1, mb: 1, textTransform: "none" }}
                    >
                      Add time slot
                    </Button>
                  )}
                </Box>
              )}

            {/* Propose Alternative Times */}
            {(!alreadyQuoted || isChangingQuote || quoteRejected) && (
              <Box sx={{ mt: 2 }}>
                <Tooltip
                  title={
                    shop.status !== "Active"
                      ? `Your shop must be Active to submit counter offers (currently ${shop.status})`
                      : ""
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={shop.status !== "Active"}
                      onClick={() =>
                        setCounterOffer((prev) => ({
                          ...prev,
                          [request.id]: !prev[request.id],
                        }))
                      }
                    >
                      {counterOffer[request.id]
                        ? "Hide Alternative Times"
                        : "Propose Alternative Times"}
                    </Button>
                  </span>
                </Tooltip>

                {counterOffer[request.id] && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <u>Propose up to 3 alternative times</u>
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1, fontWeight: 600 }}
                    >
                      Appointment start time
                    </Typography>

                    <Box sx={APPT_TIME_ROW_SX}>
                      <Box sx={{ minWidth: 160, width: 180, flexShrink: 0 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Alternative Date"
                            value={selectedCounterDate}
                            onChange={(newValue) =>
                              setSelectedCounterDate(newValue)
                            }
                            renderInput={(params) => (
                              <TextField {...params} fullWidth size="small" />
                            )}
                          />
                        </LocalizationProvider>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          alignItems: "center",
                          flexWrap: "nowrap",
                        }}
                      >
                        <FormControl size="small" sx={APPT_HOUR_CONTROL_SX}>
                          <InputLabel id={`alt-hour-${request.id}`} shrink>
                            Hour
                          </InputLabel>
                          <Select
                            labelId={`alt-hour-${request.id}`}
                            label="Hour"
                            notched
                            displayEmpty
                            value={altTimeHour ?? ""}
                            renderValue={(selected) =>
                              selected !== "" && selected != null
                                ? String(selected)
                                : "00"
                            }
                            onChange={(e) => setAltTimeHour(e.target.value)}
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                              (h) => (
                                <MenuItem key={h} value={h.toString()}>
                                  {h}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </FormControl>
                        <Typography variant="body2">:</Typography>
                        <FormControl size="small" sx={{ minWidth: 60 }}>
                          <InputLabel>Min</InputLabel>
                          <Select
                            value={altTimeMinute}
                            label="Min"
                            onChange={(e) => setAltTimeMinute(e.target.value)}
                          >
                            <MenuItem value="00">00</MenuItem>
                            <MenuItem value="15">15</MenuItem>
                            <MenuItem value="30">30</MenuItem>
                            <MenuItem value="45">45</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 70 }}>
                          <InputLabel>AM/PM</InputLabel>
                          <Select
                            value={altTimeAmpm || "AM"}
                            label="AM/PM"
                            onChange={(e) => setAltTimeAmpm(e.target.value)}
                          >
                            <MenuItem value="AM">AM</MenuItem>
                            <MenuItem value="PM">PM</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <TextField
                        label="Hrs"
                        size="small"
                        type="text"
                        inputMode="decimal"
                        inputProps={{ style: { textAlign: "center" } }}
                        value={displayHrsStored(jobDurations[request.id])}
                        onChange={(e) => {
                          let value = filterHrsInput(e.target.value);
                          if (value === "") value = "0";
                          setJobDurations((prev) => ({
                            ...prev,
                            [request.id]: value,
                          }));
                        }}
                        onBlur={(e) => {
                          const next = normalizeHrsOnBlur(e.target.value);
                          setJobDurations((prev) => ({
                            ...prev,
                            [request.id]: next,
                          }));
                        }}
                        sx={{ minWidth: "100px", width: "100px" }}
                      />

                      <TextField
                        label="Est. Finish Time"
                        size="small"
                        value={
                          altTimeHour &&
                          altTimeMinute &&
                          altTimeAmpm &&
                          jobDurations[request.id]
                            ? (() => {
                                try {
                                  const startTime = convertTo24Hour(
                                    altTimeHour,
                                    altTimeMinute,
                                    altTimeAmpm,
                                  );
                                  const [h, m] = startTime
                                    .split(":")
                                    .map(Number);
                                  const totalMins =
                                    h * 60 +
                                    m +
                                    parseFloat(jobDurations[request.id]) * 60;
                                  const endH = Math.floor(totalMins / 60) % 24;
                                  const endM = Math.round(totalMins % 60);
                                  const period = endH >= 12 ? "PM" : "AM";
                                  const displayH =
                                    endH === 0
                                      ? 12
                                      : endH > 12
                                        ? endH - 12
                                        : endH;
                                  return `${displayH}:${String(endM).padStart(2, "0")} ${period}`;
                                } catch {
                                  return "";
                                }
                              })()
                            : ""
                        }
                        InputProps={{ readOnly: true }}
                        sx={{
                          minWidth: "140px",
                          width: "140px",
                          flexShrink: 0,
                        }}
                      />

                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          whiteSpace: "nowrap",
                          textTransform: "none",
                          alignSelf: "center",
                          backgroundColor:
                            selectedCounterDate &&
                            altTimeHour &&
                            altTimeMinute &&
                            altTimeAmpm &&
                            jobDurations[request.id]
                              ? "#1976d2"
                              : undefined,
                        }}
                        onClick={() => {
                          if (
                            selectedCounterDate &&
                            altTimeHour &&
                            altTimeMinute &&
                            altTimeAmpm &&
                            jobDurations[request.id]
                          ) {
                            const formattedDate =
                              selectedCounterDate.format("YYYY-MM-DD");
                            const formattedStart = convertTo24Hour(
                              altTimeHour,
                              altTimeMinute,
                              altTimeAmpm,
                            );
                            const [h, m] = formattedStart
                              .split(":")
                              .map(Number);
                            const totalMins =
                              h * 60 +
                              m +
                              parseFloat(jobDurations[request.id]) * 60;
                            const endH = Math.floor(totalMins / 60) % 24;
                            const endM = Math.round(totalMins % 60);
                            const formattedEnd = `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;

                            const requestDates =
                              counterAvailableDates[request.id] || [];
                            const isDuplicate = requestDates.some(
                              (d) =>
                                d.date === formattedDate &&
                                d.start_time === formattedStart &&
                                d.end_time === formattedEnd,
                            );

                            if (!isDuplicate && requestDates.length < 3) {
                              setCounterAvailableDates((prev) => ({
                                ...prev,
                                [request.id]: [
                                  ...(prev[request.id] || []),
                                  {
                                    date: formattedDate,
                                    start_time: formattedStart,
                                    end_time: formattedEnd,
                                    is_dropoff: false, // Mark as appointment (not drop-off)
                                  },
                                ],
                              }));

                              setSelectedCounterDate(null);
                              setAltTimeHour("9");
                              setAltTimeMinute("00");
                              setAltTimeAmpm("AM");
                            }
                          }
                        }}
                        disabled={
                          !selectedCounterDate ||
                          !altTimeHour ||
                          !altTimeMinute ||
                          !altTimeAmpm ||
                          !jobDurations[request.id] ||
                          (counterAvailableDates[request.id]?.length || 0) >=
                            3 ||
                          (counterAvailableDates[request.id] || []).some(
                            (d) =>
                              d.date ===
                                selectedCounterDate?.format("YYYY-MM-DD") &&
                              d.start_time ===
                                convertTo24Hour(
                                  altTimeHour,
                                  altTimeMinute,
                                  altTimeAmpm,
                                ),
                          )
                        }
                      >
                        ADD ALTERNATIVE TIME
                      </Button>
                    </Box>

                    <Box mt={1}>
                      {(counterAvailableDates[request.id] || [])
                        .filter(
                          (date) =>
                            date.date && date.start_time && date.end_time,
                        )
                        .map((date, i) => (
                          <Chip
                            key={i}
                            label={`${format(parseISO(date.date), "EEEE, MMMM d, yyyy")} (${format(parseISO(`${date.date}T${date.start_time}`), "h:mm a")} – ${format(parseISO(`${date.date}T${date.end_time}`), "h:mm a")})`}
                            onDelete={() =>
                              setCounterAvailableDates((prev) => ({
                                ...prev,
                                [request.id]: prev[request.id].filter(
                                  (_, index) => index !== i,
                                ),
                              }))
                            }
                            sx={{ mr: 1, mt: 1 }}
                          />
                        ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Offer Drop-off Dates - Available for both Direct and Broadcast */}
            {(!alreadyQuoted || isChangingQuote || quoteRejected) && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ my: 2 }} />
                <Tooltip
                  title={
                    shop.status !== "Active"
                      ? `Your shop must be Active to offer drop-off service (currently ${shop.status})`
                      : ""
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={shop.status !== "Active"}
                      onClick={() => {
                        // Toggle visibility of drop-off input section
                        if (
                          dropoffDates[request.id] &&
                          dropoffDates[request.id].length > 0
                        ) {
                          // If there are drop-off dates, clear them (hide section)
                          setDropoffDates((prev) => ({
                            ...prev,
                            [request.id]: [],
                          }));
                        } else {
                          // Show section by initializing empty array
                          setDropoffDates((prev) => ({
                            ...prev,
                            [request.id]: [],
                          }));
                        }
                      }}
                      sx={{
                        backgroundColor: "#ff9800",
                        "&:hover": { backgroundColor: "#f57c00" },
                      }}
                    >
                      🚗 Offer Drop-off Service
                    </Button>
                  </span>
                </Tooltip>

                {/* Show drop-off input if array exists (even if empty) */}
                {dropoffDates[request.id] !== undefined && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <u>Add up to 3 drop-off dates</u>
                    </Typography>
                    <Box
                      sx={{
                        ...APPT_TIME_ROW_SX,
                        mt: 0.5,
                        gap: 1.5,
                        alignItems: "flex-end",
                      }}
                    >
                      {/* Drop-off Date */}
                      <Box sx={{ minWidth: 160, width: 180, flexShrink: 0 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Drop-off Date"
                            value={selectedDropoffDate}
                            onChange={(newValue) =>
                              setSelectedDropoffDate(newValue)
                            }
                            renderInput={(params) => (
                              <TextField {...params} fullWidth size="small" />
                            )}
                            minDate={dayjs()}
                          />
                        </LocalizationProvider>
                      </Box>

                      {/* Drop-off Time */}
                      <Box sx={{ flexShrink: 0 }}>
                        <Typography
                          variant="caption"
                          display="block"
                          gutterBottom
                        >
                          Drop-off Time:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            alignItems: "center",
                            flexWrap: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          <FormControl size="small" sx={APPT_HOUR_CONTROL_SX}>
                            <InputLabel>Hour</InputLabel>
                            <Select
                              value={dropoffHour}
                              label="Hour"
                              onChange={(e) => setDropoffHour(e.target.value)}
                            >
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                                (h) => (
                                  <MenuItem key={h} value={h.toString()}>
                                    {h}
                                  </MenuItem>
                                ),
                              )}
                            </Select>
                          </FormControl>
                          <Typography variant="body2">:</Typography>
                          <FormControl size="small" sx={{ minWidth: 50 }}>
                            <InputLabel>Min</InputLabel>
                            <Select
                              value={dropoffMinute}
                              label="Min"
                              onChange={(e) => setDropoffMinute(e.target.value)}
                            >
                              <MenuItem value="00">00</MenuItem>
                              <MenuItem value="15">15</MenuItem>
                              <MenuItem value="30">30</MenuItem>
                              <MenuItem value="45">45</MenuItem>
                            </Select>
                          </FormControl>
                          <FormControl size="small" sx={{ minWidth: 60 }}>
                            <InputLabel>AM/PM</InputLabel>
                            <Select
                              value={dropoffAmpm}
                              label="AM/PM"
                              onChange={(e) => setDropoffAmpm(e.target.value)}
                            >
                              <MenuItem value="AM">AM</MenuItem>
                              <MenuItem value="PM">PM</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>

                      {/* Pickup Time */}
                      <Box sx={{ flexShrink: 0 }}>
                        <Typography
                          variant="caption"
                          display="block"
                          gutterBottom
                        >
                          Pickup Time:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            alignItems: "center",
                            flexWrap: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          <FormControl size="small" sx={APPT_HOUR_CONTROL_SX}>
                            <InputLabel>Hour</InputLabel>
                            <Select
                              value={pickupHour}
                              label="Hour"
                              onChange={(e) => setPickupHour(e.target.value)}
                            >
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                                (h) => (
                                  <MenuItem key={h} value={h.toString()}>
                                    {h}
                                  </MenuItem>
                                ),
                              )}
                            </Select>
                          </FormControl>
                          <Typography variant="body2">:</Typography>
                          <FormControl size="small" sx={{ minWidth: 50 }}>
                            <InputLabel>Min</InputLabel>
                            <Select
                              value={pickupMinute}
                              label="Min"
                              onChange={(e) => setPickupMinute(e.target.value)}
                            >
                              <MenuItem value="00">00</MenuItem>
                              <MenuItem value="15">15</MenuItem>
                              <MenuItem value="30">30</MenuItem>
                              <MenuItem value="45">45</MenuItem>
                            </Select>
                          </FormControl>
                          <FormControl size="small" sx={{ minWidth: 60 }}>
                            <InputLabel>AM/PM</InputLabel>
                            <Select
                              value={pickupAmpm}
                              label="AM/PM"
                              onChange={(e) => setPickupAmpm(e.target.value)}
                            >
                              <MenuItem value="AM">AM</MenuItem>
                              <MenuItem value="PM">PM</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>

                      {/* Add Button */}
                      <Box sx={{ flexShrink: 0, minWidth: 140 }}>
                        <Button
                          variant="contained"
                          fullWidth
                          sx={{
                            backgroundColor:
                              selectedDropoffDate &&
                              dropoffHour &&
                              dropoffMinute &&
                              dropoffAmpm &&
                              pickupHour &&
                              pickupMinute &&
                              pickupAmpm
                                ? "#ff9800"
                                : "#ccc",
                            "&:hover": {
                              backgroundColor:
                                selectedDropoffDate &&
                                dropoffHour &&
                                dropoffMinute &&
                                dropoffAmpm &&
                                pickupHour &&
                                pickupMinute &&
                                pickupAmpm
                                  ? "#f57c00"
                                  : "#ccc",
                            },
                          }}
                          disabled={
                            !selectedDropoffDate ||
                            !dropoffHour ||
                            !dropoffMinute ||
                            !dropoffAmpm ||
                            !pickupHour ||
                            !pickupMinute ||
                            !pickupAmpm ||
                            (dropoffDates[request.id] &&
                              dropoffDates[request.id].length >= 3)
                          }
                          onClick={() => {
                            const dropoffTime24 = convertTo24Hour(
                              dropoffHour,
                              dropoffMinute,
                              dropoffAmpm,
                            );
                            const pickupTime24 = convertTo24Hour(
                              pickupHour,
                              pickupMinute,
                              pickupAmpm,
                            );

                            const newDropoff = {
                              date: selectedDropoffDate.format("YYYY-MM-DD"),
                              dropoff_time: dropoffTime24,
                              pickup_time: pickupTime24,
                            };

                            setDropoffDates((prev) => ({
                              ...prev,
                              [request.id]: [
                                ...(prev[request.id] || []),
                                newDropoff,
                              ],
                            }));

                            // Reset inputs
                            setSelectedDropoffDate(null);
                            setDropoffHour("8");
                            setDropoffMinute("00");
                            setDropoffAmpm("AM");
                            setPickupHour("5");
                            setPickupMinute("00");
                            setPickupAmpm("PM");
                          }}
                        >
                          <Add sx={{ mr: 0.5 }} /> Add Drop-off
                        </Button>
                      </Box>
                    </Box>

                    {/* Display added drop-off dates */}
                    <Box sx={{ mt: 2 }}>
                      {dropoffDates[request.id] &&
                        dropoffDates[request.id]
                          .filter(
                            (dropoff) =>
                              dropoff.date &&
                              dropoff.dropoff_time &&
                              dropoff.pickup_time,
                          )
                          .map((dropoff, i) => (
                            <Chip
                              key={i}
                              label={`${format(parseISO(dropoff.date), "EEEE, MMMM d, yyyy")} (Drop-off: ${formatTimeTo12Hour(dropoff.dropoff_time)} – Pickup: ${formatTimeTo12Hour(dropoff.pickup_time)})`}
                              onDelete={() =>
                                setDropoffDates((prev) => ({
                                  ...prev,
                                  [request.id]: prev[request.id].filter(
                                    (_, index) => index !== i,
                                  ),
                                }))
                              }
                              sx={{
                                mr: 1,
                                mt: 1,
                                backgroundColor: "#fff3e0",
                                borderColor: "#ff9800",
                              }}
                              variant="outlined"
                            />
                          ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Right side - Action Buttons */}
          <Stack
            spacing={2}
            direction="column"
            sx={{
              maxwidth: "100%",
              flexShrink: 0,
              alignSelf: "flex-start",
            }}
          >
            {/* Message Button */}
            <Button
              variant="contained"
              size="small"
              onClick={() => onOpenConversation(request)}
              sx={{
                bgcolor: "#e63926",
                "&:hover": { bgcolor: "#c62f1e" },
                textTransform: "none",
              }}
            >
              Message
            </Button>

            {/* Extended Service History Button - Only show for Owners */}
            {/* {console.log('extended history: ' + hasExtendedServiceHistory)} */}

            {userRole === "Owner" &&
              (isExtendedHistoryActive || isExtendedHistoryPlanAvailable) && (
                <Button
                  onClick={() => onExtendedHistoryClick(request)}
                  variant={isExtendedHistoryActive ? "outlined" : "contained"}
                  size="small"
                  sx={{
                    backgroundColor: isExtendedHistoryActive
                      ? "transparent"
                      : "#4caf50",
                    color: isExtendedHistoryActive ? "#4caf50" : "#fff",
                    borderColor: isExtendedHistoryActive
                      ? "#4caf50"
                      : "transparent",
                    fontSize: "0.85rem",
                    padding: "6px 12px",
                    minHeight: "auto",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: isExtendedHistoryActive
                        ? "#e8f5e9"
                        : "#45a049",
                    },
                  }}
                >
                  {isExtendedHistoryActive
                    ? "📋 Extended History"
                    : "📋 Buy Extended History"}
                </Button>
              )}

            {/* Quote Status Messages - Only show if quote submitted but not yet accepted/confirmed */}
            {alreadyQuoted &&
              !quoteRejected &&
              !customerAccepted &&
              !shopConfirmed && (
                <Alert severity="success">
                  <Typography variant="body2" fontWeight="bold">
                    Quote Submitted - Waiting for Customer
                  </Typography>
                  {/* Always show what was sent from the Bid row so another mechanic (or same) sees the full quote */}
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      display="block"
                    >
                      Quote you sent (from this bid):
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ ml: 1 }}
                    >
                      <strong>Price:</strong> $
                      {shopBid?.offer_price ?? shopBid?.estimated_price ?? "—"}
                    </Typography>
                    {(() => {
                      const slots = shopBid?.shop_offered_slots
                        ? typeof shopBid.shop_offered_slots === "string"
                          ? JSON.parse(shopBid.shop_offered_slots)
                          : shopBid.shop_offered_slots
                        : [];
                      const hasSlots = Array.isArray(slots) && slots.length > 0;
                      if (hasSlots) {
                        return (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              display="block"
                            >
                              Appointment(s) offered:
                            </Typography>
                            {slots.map((slot, idx) => {
                              const dateStr = slot.date
                                ? slot.date.toString().split("T")[0]
                                : (shopBid?.selected_available_date || "")
                                    .toString()
                                    .split("T")[0];
                              const dateLabel = dateStr
                                ? format(parseISO(dateStr), "EEE, MMM d, yyyy")
                                : "";
                              const timeLabel = [slot.start, slot.end]
                                .filter(Boolean)
                                .map((t) => formatTimeToAMPM(t))
                                .join(" – ");
                              return (
                                <Typography
                                  key={idx}
                                  variant="caption"
                                  display="block"
                                  sx={{ ml: 2 }}
                                >
                                  • {dateLabel}
                                  {timeLabel ? ` at ${timeLabel}` : ""}
                                </Typography>
                              );
                            })}
                          </Box>
                        );
                      }
                      // Same as broadcast: list all non-drop-off mechanic slots for this shop when shop_offered_slots is missing (e.g. direct request)
                      const appointmentSlots = (
                        request.mechanic_available_dates || []
                      ).filter(
                        (d) =>
                          d.source === "Mechanic" &&
                          d.shop_id === shopBid?.autoshop_id &&
                          !d.is_dropoff,
                      );
                      if (appointmentSlots.length > 0) {
                        return (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              display="block"
                            >
                              Appointment(s) offered:
                            </Typography>
                            {appointmentSlots.map((slot, idx) => {
                              const dateStr = (
                                slot.available_date ||
                                slot.date ||
                                ""
                              )
                                .toString()
                                .split("T")[0];
                              const dateLabel = dateStr
                                ? format(parseISO(dateStr), "EEE, MMM d, yyyy")
                                : "";
                              const timeLabel = [slot.start_time, slot.end_time]
                                .filter(Boolean)
                                .map((t) => formatTimeToAMPM(t))
                                .join(" – ");
                              return (
                                <Typography
                                  key={idx}
                                  variant="caption"
                                  display="block"
                                  sx={{ ml: 2 }}
                                >
                                  • {dateLabel}
                                  {timeLabel ? ` at ${timeLabel}` : ""}
                                </Typography>
                              );
                            })}
                          </Box>
                        );
                      }
                      if (
                        shopBid?.selected_available_date ||
                        shopBid?.proposed_time ||
                        shopBid?.is_dropoff
                      ) {
                        return (
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ ml: 1 }}
                          >
                            <strong>
                              {shopBid.is_dropoff
                                ? "Drop-off:"
                                : "Appointment:"}
                            </strong>{" "}
                            {shopBid.is_dropoff ? (
                              <>
                                🚗 Drop-off on{" "}
                                {
                                  (shopBid.selected_available_date || "")
                                    .toString()
                                    .split("T")[0]
                                }
                                {shopBid.proposed_time &&
                                  ` — drop off by ${formatTimeToAMPM(shopBid.proposed_time)}`}
                                {shopBid.proposed_end_time &&
                                  `, pickup by ${formatTimeToAMPM(shopBid.proposed_end_time)}`}
                              </>
                            ) : (
                              <>
                                {
                                  (shopBid.selected_available_date || "")
                                    .toString()
                                    .split("T")[0]
                                }
                                {shopBid.proposed_time &&
                                  ` at ${formatTimeToAMPM(shopBid.proposed_time)}`}
                                {shopBid.proposed_end_time &&
                                  ` – ${formatTimeToAMPM(shopBid.proposed_end_time)}`}
                              </>
                            )}
                          </Typography>
                        );
                      }
                      return null;
                    })()}
                  </Box>
                  {(() => {
                    // Supplementary: alternative times, drop-off options (shop_offered_slots already shown above as "Appointment(s) offered")
                    const details = [];
                    try {
                      // shop_offered_slots are already listed above with dates; only add alternative/drop-off here
                    } catch (e) {
                      console.error("Error parsing shop_offered_slots:", e);
                    }
                    if (request.mechanic_available_dates?.length > 0) {
                      const allMechanicSlots =
                        request.mechanic_available_dates.filter(
                          (d) =>
                            d.source === "Mechanic" &&
                            d.shop_id === shopBid.autoshop_id &&
                            !d.is_dropoff,
                        );
                      const shopSlots = shopBid?.shop_offered_slots
                        ? typeof shopBid.shop_offered_slots === "string"
                          ? JSON.parse(shopBid.shop_offered_slots)
                          : shopBid.shop_offered_slots
                        : [];
                      // Only show "Alternative time(s)" when we had shop_offered_slots above; otherwise all appointments were already listed as "Appointment(s) offered"
                      const hasShopSlots =
                        Array.isArray(shopSlots) && shopSlots.length > 0;
                      const toTime = (t) =>
                        (t || "").toString().substring(0, 5);
                      const norm = (date, start, end) =>
                        `${(date || "").toString().split("T")[0]}|${toTime(start)}|${toTime(end)}`;
                      const preferredKeys = new Set(
                        (Array.isArray(shopSlots) ? shopSlots : []).map((s) =>
                          norm(s.date, s.start, s.end),
                        ),
                      );
                      const altTimes = hasShopSlots
                        ? allMechanicSlots.filter(
                            (d) =>
                              !preferredKeys.has(
                                norm(
                                  d.available_date || d.date,
                                  d.start_time,
                                  d.end_time,
                                ),
                              ),
                          )
                        : [];
                      if (altTimes.length > 0) {
                        details.push(
                          <Box key="alt-times" sx={{ mt: 1 }}>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              display="block"
                            >
                              📅 Alternative time(s) proposed:
                            </Typography>
                            {altTimes.map((slot, idx) => (
                              <Typography
                                key={idx}
                                variant="caption"
                                display="block"
                                sx={{ ml: 2 }}
                              >
                                •{" "}
                                {
                                  (slot.available_date || slot.date || "")
                                    .toString()
                                    .split("T")[0]
                                }{" "}
                                | {formatTimeToAMPM(slot.start_time)} -{" "}
                                {formatTimeToAMPM(slot.end_time)}
                              </Typography>
                            ))}
                          </Box>,
                        );
                      }
                      const dropoffTimes =
                        request.mechanic_available_dates.filter(
                          (d) =>
                            d.source === "Mechanic" &&
                            d.shop_id === shopBid.autoshop_id &&
                            d.is_dropoff,
                        );
                      if (dropoffTimes.length > 0) {
                        details.push(
                          <Box key="dropoff-times" sx={{ mt: 1 }}>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              display="block"
                            >
                              🚗 Drop-off option(s) offered:
                            </Typography>
                            {dropoffTimes.map((slot, idx) => (
                              <Typography
                                key={idx}
                                variant="caption"
                                display="block"
                                sx={{ ml: 2 }}
                              >
                                • {slot.date} at{" "}
                                {formatTimeToAMPM(slot.start_time)} - Pickup:{" "}
                                {formatTimeToAMPM(slot.end_time)}
                              </Typography>
                            ))}
                          </Box>,
                        );
                      }
                    }
                    return details.length > 0 ? details : null;
                  })()}
                </Alert>
              )}

            {quoteRejected && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Quote Declined
                </Typography>
                {/* Same as "Quote Submitted": show what was sent from the Bid row */}
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    display="block"
                  >
                    Quote you had sent (from this bid):
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ ml: 1 }}>
                    <strong>Price:</strong> $
                    {shopBid?.offer_price ?? shopBid?.estimated_price ?? "—"}
                  </Typography>
                  {(() => {
                    const slots = shopBid?.shop_offered_slots
                      ? typeof shopBid.shop_offered_slots === "string"
                        ? JSON.parse(shopBid.shop_offered_slots)
                        : shopBid.shop_offered_slots
                      : [];
                    const hasSlots = Array.isArray(slots) && slots.length > 0;
                    if (hasSlots) {
                      return (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography
                            variant="caption"
                            fontWeight="bold"
                            display="block"
                          >
                            Appointment(s) offered:
                          </Typography>
                          {slots.map((slot, idx) => {
                            const dateStr = slot.date
                              ? slot.date.toString().split("T")[0]
                              : (shopBid?.selected_available_date || "")
                                  .toString()
                                  .split("T")[0];
                            const dateLabel = dateStr
                              ? format(parseISO(dateStr), "EEE, MMM d, yyyy")
                              : "";
                            const timeLabel = [slot.start, slot.end]
                              .filter(Boolean)
                              .map((t) => formatTimeToAMPM(t))
                              .join(" – ");
                            return (
                              <Typography
                                key={idx}
                                variant="caption"
                                display="block"
                                sx={{ ml: 2 }}
                              >
                                • {dateLabel}
                                {timeLabel ? ` at ${timeLabel}` : ""}
                              </Typography>
                            );
                          })}
                        </Box>
                      );
                    }
                    const appointmentSlots = (
                      request.mechanic_available_dates || []
                    ).filter(
                      (d) =>
                        d.source === "Mechanic" &&
                        d.shop_id === shopBid?.autoshop_id &&
                        !d.is_dropoff,
                    );
                    if (appointmentSlots.length > 0) {
                      return (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography
                            variant="caption"
                            fontWeight="bold"
                            display="block"
                          >
                            Appointment(s) offered:
                          </Typography>
                          {appointmentSlots.map((slot, idx) => {
                            const dateStr = (
                              slot.available_date ||
                              slot.date ||
                              ""
                            )
                              .toString()
                              .split("T")[0];
                            const dateLabel = dateStr
                              ? format(parseISO(dateStr), "EEE, MMM d, yyyy")
                              : "";
                            const timeLabel = [slot.start_time, slot.end_time]
                              .filter(Boolean)
                              .map((t) => formatTimeToAMPM(t))
                              .join(" – ");
                            return (
                              <Typography
                                key={idx}
                                variant="caption"
                                display="block"
                                sx={{ ml: 2 }}
                              >
                                • {dateLabel}
                                {timeLabel ? ` at ${timeLabel}` : ""}
                              </Typography>
                            );
                          })}
                        </Box>
                      );
                    }
                    if (
                      shopBid?.selected_available_date ||
                      shopBid?.proposed_time ||
                      shopBid?.is_dropoff
                    ) {
                      return (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ ml: 1 }}
                        >
                          <strong>
                            {shopBid.is_dropoff ? "Drop-off:" : "Appointment:"}
                          </strong>{" "}
                          {shopBid.is_dropoff ? (
                            <>
                              🚗 Drop-off on{" "}
                              {
                                (shopBid.selected_available_date || "")
                                  .toString()
                                  .split("T")[0]
                              }
                              {shopBid.proposed_time &&
                                ` — drop off by ${formatTimeToAMPM(shopBid.proposed_time)}`}
                              {shopBid.proposed_end_time &&
                                `, pickup by ${formatTimeToAMPM(shopBid.proposed_end_time)}`}
                            </>
                          ) : (
                            <>
                              {
                                (shopBid.selected_available_date || "")
                                  .toString()
                                  .split("T")[0]
                              }
                              {shopBid.proposed_time &&
                                ` at ${formatTimeToAMPM(shopBid.proposed_time)}`}
                              {shopBid.proposed_end_time &&
                                ` – ${formatTimeToAMPM(shopBid.proposed_end_time)}`}
                            </>
                          )}
                        </Typography>
                      );
                    }
                    return null;
                  })()}
                </Box>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: "bold" }}>
                  You can submit a new quote with different pricing/times or
                  cancel this quote using the button below.
                </Typography>
              </Alert>
            )}

            {customerAccepted &&
              !shopConfirmed &&
              !quoteRejected &&
              !isChangingQuote && (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body1" fontWeight="bold">
                      Customer Accepted Your Quote for ${shopBid.offer_price}
                    </Typography>
                    {/* Light green box: full bid details from Bids row (date, drop-off vs appointment, times) */}
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 2,
                        backgroundColor: "#e8f5e9",
                        borderRadius: 1,
                        border: "1px solid #a5d6a7",
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        display="block"
                        sx={{ mb: 0.5 }}
                      >
                        Accepted quote (from bid):
                      </Typography>
                      <Typography variant="body2" component="span">
                        <strong>Price:</strong> $
                        {shopBid?.offer_price ??
                          shopBid?.estimated_price ??
                          "—"}
                      </Typography>
                      {(() => {
                        if (shopBid?.customer_selected_slot_details) {
                          const slot = shopBid.customer_selected_slot_details;
                          const dateStr = (
                            slot.available_date ||
                            slot.date ||
                            ""
                          )
                            .toString()
                            .split("T")[0];
                          const startTime12 = formatTimeTo12Hour(
                            slot.start_time,
                          );
                          const endTime12 = formatTimeTo12Hour(slot.end_time);
                          const isDropoff = !!(
                            slot.is_dropoff === true ||
                            slot.is_dropoff === 1 ||
                            slot.is_dropoff === "true"
                          );
                          return (
                            <Typography
                              variant="body2"
                              display="block"
                              sx={{ mt: 0.5 }}
                            >
                              <strong>
                                {isDropoff ? "Drop-off" : "Appointment"}:
                              </strong>{" "}
                              {isDropoff && "🚗 "}
                              {dateStr}
                              {slot.start_time && slot.end_time
                                ? ` at ${startTime12} – ${endTime12}`
                                : isDropoff
                                  ? " — times as agreed"
                                  : ""}
                            </Typography>
                          );
                        }
                        if (
                          shopBid?.selected_available_date ||
                          shopBid?.proposed_time ||
                          shopBid?.is_dropoff
                        ) {
                          return (
                            <Typography
                              variant="body2"
                              display="block"
                              sx={{ mt: 0.5 }}
                            >
                              <strong>
                                {shopBid.is_dropoff
                                  ? "Drop-off"
                                  : "Appointment"}
                                :
                              </strong>{" "}
                              {
                                (shopBid.selected_available_date || "")
                                  .toString()
                                  .split("T")[0]
                              }
                              {shopBid.proposed_time &&
                                ` at ${formatTimeToAMPM(shopBid.proposed_time)}`}
                              {shopBid.proposed_end_time &&
                                ` – ${formatTimeToAMPM(shopBid.proposed_end_time)}`}
                              {shopBid.is_dropoff &&
                                !shopBid.proposed_time &&
                                " — times as agreed"}
                            </Typography>
                          );
                        }
                        if (
                          shopBid?.customer_selected_slot_id === null ||
                          shopBid?.customer_selected_slot_id === undefined
                        ) {
                          return shopBid?.is_dropoff ? (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              🚗 Drop-off service selected
                            </Typography>
                          ) : null;
                        }
                        return (
                          <Typography
                            variant="body2"
                            sx={{ mt: 0.5, color: "orange" }}
                          >
                            ⚠️ Customer selected a time slot — refresh to see
                            details
                          </Typography>
                        );
                      })()}
                    </Box>
                  </Alert>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      fullWidth
                      onClick={async () => {
                        if (!billingReadyForQuotes) {
                          onBillingRequired?.(request.id);
                          return;
                        }
                        if (onShopConfirm) {
                          await onShopConfirm(request.id);
                          return;
                        }
                        try {
                          const response = await fetch(
                            `${getApiBase()}/api/bids/shop-confirm`,
                            {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              credentials: "include",
                              body: JSON.stringify({ requestId: request.id }),
                            },
                          );

                          if (response.ok) {
                            alert(
                              "Appointment confirmed! Customer has been notified.",
                            );
                            if (onRefresh) await onRefresh();
                          } else {
                            const data = await response.json();
                            alert(
                              "Failed to confirm: " +
                                (data.message || "Unknown error"),
                            );
                          }
                        } catch (err) {
                          alert("Error confirming appointment: " + err.message);
                        }
                      }}
                    >
                      Confirm
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      fullWidth
                      onClick={() => {
                        // Pre-populate the quote amount with their existing offer
                        if (shopBid?.offer_price && !bidAmounts[request.id]) {
                          setBidAmounts((prev) => ({
                            ...prev,
                            [request.id]: shopBid.offer_price,
                          }));
                        }

                        // Pre-populate existing time slots if they exist
                        if (shopBid?.shop_offered_slots) {
                          try {
                            const existingSlots =
                              typeof shopBid.shop_offered_slots === "string"
                                ? JSON.parse(shopBid.shop_offered_slots)
                                : shopBid.shop_offered_slots;

                            if (
                              Array.isArray(existingSlots) &&
                              existingSlots.length > 0
                            ) {
                              setShopTimeSlots((prev) => ({
                                ...prev,
                                [request.id]: existingSlots,
                              }));
                            }
                          } catch (e) {
                            console.error(
                              "Error parsing existing time slots:",
                              e,
                            );
                          }
                        }

                        // Pre-populate drop-off dates if they exist
                        if (
                          request.mechanic_available_dates &&
                          request.mechanic_available_dates.length > 0
                        ) {
                          const existingDropoffs =
                            request.mechanic_available_dates.filter(
                              (d) =>
                                d.source === "Mechanic" &&
                                d.shop_id === shopBid.autoshop_id &&
                                d.is_dropoff,
                            );
                          if (existingDropoffs.length > 0) {
                            setDropoffDates((prev) => ({
                              ...prev,
                              [request.id]: existingDropoffs.map((d) => ({
                                date: d.date,
                                dropoff_time: d.start_time,
                                pickup_time: d.end_time,
                              })),
                            }));
                          }
                        }

                        // Enable change quote mode to show the form again
                        setIsChangingQuote(true);
                      }}
                    >
                      Request Time Change
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      fullWidth
                      onClick={() => onCancelQuote(request.id)}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Box>
              )}

            {shopConfirmed && (
              <Alert severity="info" sx={{ textAlign: "center" }}>
                <Typography variant="body2" fontWeight="bold">
                  Appointment Scheduled
                </Typography>
                <Typography variant="caption">
                  Service is confirmed and scheduled
                </Typography>
              </Alert>
            )}

            {/* Estimate Price and Send Quote - Show if not already quoted, in change quote mode, OR if changing a rejected quote */}
            {(!alreadyQuoted || isChangingQuote) &&
              !shopConfirmed &&
              (!quoteRejected || isChangingQuote) && (
                <>
                  <TextField
                    label="Quote"
                    size="small"
                    type="number"
                    error={!bidAmounts[request.id]}
                    value={bidAmounts[request.id] || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty or valid numbers
                      if (value === "" || !isNaN(parseFloat(value))) {
                        onPriceChange(request.id, value);
                      }
                    }}
                    onBlur={(e) => {
                      // Format to 2 decimal places on blur
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value)) {
                        onPriceChange(request.id, value.toFixed(2));
                      }
                    }}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <span style={{ marginRight: "4px" }}>$</span>
                      ),
                    }}
                    inputProps={{
                      step: "0.01",
                      min: "0",
                    }}
                  />

                  {(() => {
                    const breakdown = estimatePlatformFeeBreakdown(
                      bidAmounts[request.id],
                      platformFeeRate,
                    );
                    if (!breakdown) {
                      return null;
                    }
                    const pct = Math.round(breakdown.rate * 1000) / 10;
                    return (
                      <Box
                        sx={{
                          mt: 1,
                          p: 1.5,
                          bgcolor: "grey.50",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "grey.200",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Quote breakdown (shop view only — customer does not
                          see platform fee)
                        </Typography>
                        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                          <Typography variant="body2">
                            Customer quote: {formatUsd(breakdown.customerQuote)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Est. platform fee ({pct}%):{" "}
                            {formatUsd(breakdown.platformFee)}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            Est. shop net: {formatUsd(breakdown.shopNet)}
                          </Typography>
                        </Stack>
                      </Box>
                    );
                  })()}

                  <Tooltip
                    title={
                      shop.status !== "Active"
                        ? `Your shop must be Active to send quotes (currently ${shop.status})`
                        : isDirect
                          ? "Send quote to customer for approval"
                          : "Submit your best quote - you can only submit once!"
                    }
                    arrow
                  >
                    <span>
                      <Button
                        onClick={async () => {
                          await onSubmitBid(request.id);
                          // Reset change quote mode after successful submission
                          if (isChangingQuote) {
                            setIsChangingQuote(false);
                          }
                        }}
                        variant="contained"
                        size="small"
                        fullWidth
                        disabled={
                          shop.status !== "Active" ||
                          !bidAmounts[request.id] ||
                          (() => {
                            const { list, appointment } =
                              partitionSelectedAvailability(
                                selectedSlot[request.id],
                              );
                            const hasChips = list.length > 0;
                            const hasAppt = appointment.length > 0;
                            const hasAlts =
                              (counterAvailableDates[request.id]?.length || 0) >
                              0;
                            const hasDropoffs =
                              (dropoffDates[request.id]?.length || 0) > 0;
                            const hasBuiltShopSlots =
                              (shopTimeSlots[request.id]?.length || 0) > 0;
                            if (
                              !hasChips &&
                              !hasAlts &&
                              !hasDropoffs &&
                              !hasBuiltShopSlots
                            ) {
                              return true;
                            }
                            if (!hasAppt) return false;
                            const timeFilled =
                              (timeSlots[request.id] || []).some(
                                (slot) =>
                                  slot.hour &&
                                  slot.minute !== undefined &&
                                  slot.ampm &&
                                  slot.duration,
                              ) ||
                              (!!proposedHours[request.id] &&
                                proposedMinutes[request.id] !== undefined &&
                                !!proposedAmpm[request.id] &&
                                !!jobDurations[request.id]);
                            return !timeFilled && !hasBuiltShopSlots;
                          })()
                        }
                        sx={{
                          backgroundColor: "#4caf50",
                          "&:hover": { backgroundColor: "#45a049" },
                          "&:disabled": {
                            backgroundColor: "#e0e0e0",
                            color: "#9e9e9e",
                          },
                        }}
                      >
                        {quoteRejected
                          ? "RESUBMIT QUOTE"
                          : isChangingQuote
                            ? "UPDATE QUOTE"
                            : "SEND QUOTE"}
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}

            {/* 3-Button System for Rejected Quotes: RESUBMIT, CHANGE QUOTE, CANCEL QUOTE */}
            {quoteRejected && !isChangingQuote && (
              <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={async () => {
                    await onSubmitBid(request.id);
                  }}
                  disabled={!bidAmounts[request.id]}
                  sx={{
                    flex: 1,
                    backgroundColor: "#4caf50",
                    "&:hover": { backgroundColor: "#45a049" },
                    "&:disabled": {
                      backgroundColor: "#e0e0e0",
                      color: "#9e9e9e",
                    },
                  }}
                >
                  RESUBMIT
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => setIsChangingQuote(true)}
                  sx={{
                    flex: 1,
                    borderColor: "#1976d2",
                    color: "#1976d2",
                    "&:hover": {
                      borderColor: "#1565c0",
                      backgroundColor: "rgba(25, 118, 210, 0.04)",
                    },
                  }}
                >
                  CHANGE
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => onCancelQuote(request.id)}
                  sx={{ flex: 1 }}
                >
                  CANCEL
                </Button>
              </Stack>
            )}

            {/* Cancel button when in change quote mode */}
            {isChangingQuote && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                fullWidth
                onClick={() => setIsChangingQuote(false)}
              >
                Cancel
              </Button>
            )}

            {/* Cancel Quote Button - Show for non-rejected quotes that are pending */}
            {!quoteRejected &&
              !isChangingQuote &&
              !customerAccepted &&
              alreadyQuoted &&
              !shopConfirmed && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  fullWidth
                  onClick={() => onCancelQuote(request.id)}
                >
                  Cancel Quote
                </Button>
              )}
          </Stack>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default ServiceRequestCard;
